/**
 * HardwareSystemForm - For complex hardware like Tandems, Hinges, Slides, Pullouts
 * Simple, user-friendly interface for adding hardware products
 * Supports custom hardware types and custom field values
 */
import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Checkbox } from '../ui/checkbox';
import { Plus, Trash2, Save, Package, Layers, MoveHorizontal, DoorOpen, Archive, SlidersHorizontal, ShoppingBasket, Wrench, Settings, X, Copy } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../../lib/store';
import { logger } from '../../lib/logger';

// Default hardware type configurations
const DEFAULT_HARDWARE_TYPES = [
  { id: 'tandem', label: 'Tandem / Drawer System', icon: 'Layers', rateUnit: 'per set' },
  { id: 'telescopic', label: 'Telescopic Drawer Slide', icon: 'MoveHorizontal', rateUnit: 'per pair' },
  { id: 'hinge', label: 'Hinge', icon: 'DoorOpen', rateUnit: 'per piece' },
  { id: 'pullout', label: 'Pullout', icon: 'Archive', rateUnit: 'per set' },
  { id: 'sliding', label: 'Sliding Track System', icon: 'SlidersHorizontal', rateUnit: 'per set' },
  { id: 'basket', label: 'Basket / Organizer', icon: 'ShoppingBasket', rateUnit: 'per piece' },
  { id: 'accessory', label: 'Accessory', icon: 'Wrench', rateUnit: 'per piece' },
];

const ICON_COMPONENTS = {
  Layers, MoveHorizontal, DoorOpen, Archive, SlidersHorizontal, ShoppingBasket, Wrench, Settings,
};

const BRANDS = ['BLUM', 'HAFELE', 'HETTICH', 'KESSEBOHMER', 'EBCO', 'LIVSMART', 'MSI'];

// Suggestions for various fields (user can type custom values)
const FIELD_SUGGESTIONS = {
  load_capacity: ['30 KG', '35 KG', '40 KG', '45 KG', '50 KG', '70 KG'],
  depth: ['300mm', '350mm', '400mm', '450mm', '500mm', '550mm', '600mm'],
  height: ['70mm', '83mm', '90mm', '101mm', '121mm', '144mm', '177mm', '187mm', '193mm', '241mm', '251mm'],
  color: ['Anthracite', 'Silver', 'White', 'Orion Grey', 'Stainless Steel', 'Black', 'Grey'],
  feature: ['Regular', 'Soft Close', 'Push to Open', 'Full Extension', 'Standard'],
  crank: ['0 Crank', '9.5 Crank', '16 Crank', '18 Crank'],
  degree: ['95°', '105°', '110°', '155°', '165°', '170°', '180°'],
  hinge_type: ['Auto Hinge', 'Slow Motion', 'Clip-on', 'Slide-on', 'Concealed', 'Soft Close'],
  door_thickness: ['14-25mm', '15-32mm', '16-19mm', '19mm', '25mm'],
  pullout_type: ['Bottle Pullout', 'Narrow Pullout', 'Magic Corner', 'Pantry', 'Base Unit', 'Tall Unit'],
  width: ['100mm', '150mm', '200mm', '250mm', '300mm', '350mm', '400mm', '450mm', '500mm', '600mm'],
  sliding_type: ['Top Sliding Track', 'Bottom Sliding', 'HI Slide', 'Soft Close Track'],
  size_ft: ['4 FT', '5 FT', '6 FT', '8 FT', '10 FT', '12 FT'],
  door_type: ['Single Door', 'Double Door', 'Bi-Fold'],
  basket_type: ['Wicker Basket', 'Wire Basket', 'Cutlery Tray', 'Plate Holder', 'Cup & Saucer'],
  material: ['Plastic', 'Stainless Steel', 'Chrome', 'Wood', 'Wire'],
  accessory_type: ['Wardrobe Lift', 'Shoe Rack', 'Belt/Tie Holder', 'Laundry Bag', 'Folding Table Bracket', 'Bed Fitting', 'Ironing Board'],
  size_inches: ['8"', '10"', '12"', '14"', '16"', '18"', '20"', '22"', '24"', '26"', '28"', '30"'],
  telescopic_feature: ['Regular', 'Soft Close', 'Push to Open', 'Cord Row', 'Self Close', 'Full Extension'],
  hinge_mount: ['Clip-on', 'Slide-on', 'Screw-on', 'Press-in', 'Euro Mount'],
};

// All available fields that can be enabled for custom type
const ALL_AVAILABLE_FIELDS = [
  { id: 'load_capacity', label: 'Load Capacity' },
  { id: 'depth', label: 'Depth' },
  { id: 'height', label: 'Height' },
  { id: 'width', label: 'Width' },
  { id: 'color', label: 'Color/Finish' },
  { id: 'feature', label: 'Feature' },
  { id: 'crank', label: 'Crank' },
  { id: 'degree', label: 'Degree' },
  { id: 'hinge_type', label: 'Hinge Type' },
  { id: 'door_thickness', label: 'Door Thickness' },
  { id: 'pullout_type', label: 'Pullout Type' },
  { id: 'sliding_type', label: 'Sliding Type' },
  { id: 'size_ft', label: 'Size (FT)' },
  { id: 'size_inches', label: 'Size (Inches)' },
  { id: 'door_type', label: 'Door Type' },
  { id: 'basket_type', label: 'Basket Type' },
  { id: 'material', label: 'Material' },
  { id: 'accessory_type', label: 'Accessory Type' },
];

// Rate unit options
const RATE_UNIT_OPTIONS = ['per piece', 'per set', 'per pair', 'per meter', 'per kg', 'per box'];

// Default fields for each hardware type
const DEFAULT_TYPE_FIELDS = {
  tandem: ['load_capacity', 'color', 'feature'],
  telescopic: ['load_capacity', 'size_inches', 'feature'],
  hinge: ['hinge_type', 'crank', 'degree', 'feature'],
  pullout: ['pullout_type', 'width', 'load_capacity'],
  sliding: ['sliding_type', 'size_ft', 'door_type'],
  basket: ['basket_type', 'material', 'feature'],
  accessory: ['accessory_type'],
  custom: [],
};

// Input with suggestions component
function SuggestionInput({ value, onChange, suggestions = [], placeholder, className }) {
  const [showSuggestions, setShowSuggestions] = useState(false);
  const filteredSuggestions = suggestions.filter(s => 
    s.toLowerCase().includes((value || '').toLowerCase())
  );

  return (
    <div className="relative">
      <Input
        value={value}
        onChange={e => onChange(e.target.value)}
        onFocus={() => setShowSuggestions(true)}
        onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
        placeholder={placeholder}
        className={className}
      />
      {showSuggestions && filteredSuggestions.length > 0 && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-[#E2E6D9] rounded-lg shadow-lg max-h-32 overflow-y-auto">
          {filteredSuggestions.slice(0, 8).map(suggestion => (
            <button
              key={suggestion}
              type="button"
              onClick={() => { onChange(suggestion); setShowSuggestions(false); }}
              className="w-full px-3 py-1.5 text-left text-xs hover:bg-[#F5F7F2]"
            >
              {suggestion}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

const emptyVariant = { width: '', depth: '', height: '', size_text: '', rate: '', feature: 'regular', weight: '', hinge_mount: 'clip_on' };

export function HardwareSystemForm({ open, onOpenChange, editProduct, onSuccess }) {
  const [hardwareTypes, setHardwareTypes] = useState(DEFAULT_HARDWARE_TYPES);
  const [showCustomTypeDialog, setShowCustomTypeDialog] = useState(false);
  const [customTypeName, setCustomTypeName] = useState('');
  const [customTypeRateUnit, setCustomTypeRateUnit] = useState('per piece');
  const [customTypeFields, setCustomTypeFields] = useState([]);
  const [newCustomFieldName, setNewCustomFieldName] = useState('');
  const [customFieldsList, setCustomFieldsList] = useState([]); // Custom fields added by user
  const [rateUnitOverride, setRateUnitOverride] = useState(''); // For on-the-fly rate unit change
  
  // Copy from Previous feature
  const [showCopyDialog, setShowCopyDialog] = useState(false);
  const [existingHardwareProducts, setExistingHardwareProducts] = useState([]);
  const [copySearchTerm, setCopySearchTerm] = useState('');
  const [loadingProducts, setLoadingProducts] = useState(false);
  
  const [form, setForm] = useState({
    brand_name: '',
    hardware_system_type: 'tandem',
    series: '',
    product_name: '',
    // All possible dynamic fields
    load_capacity: '', depth: '', height: '', width: '', color: '', feature: '',
    crank: '', degree: '', hinge_type: '', door_thickness: '',
    pullout_type: '', sliding_type: '', size_ft: '', door_type: '',
    basket_type: '', material: '', accessory_type: '', size_inches: '',
    // Per-product brand visibility in customer PDF (Admin/Manager only).
    show_brand_in_pdf: true,
  });
  
  const [variants, setVariants] = useState([{ ...emptyVariant }]);
  const [saving, setSaving] = useState(false);
  const [brandInput, setBrandInput] = useState('');
  const [showBrandDropdown, setShowBrandDropdown] = useState(false);

  // Load edit data
  useEffect(() => {
    if (editProduct && open) {
      setForm({
        brand_name: editProduct.brand_name || '',
        hardware_system_type: editProduct.hardware_system_type || 'tandem',
        series: editProduct.series || editProduct.subcategory || '',
        product_name: editProduct.product_name || '',
        load_capacity: editProduct.load_capacity || '',
        depth: editProduct.depth || '',
        height: editProduct.height || '',
        width: editProduct.width || '',
        color: editProduct.color || '',
        feature: editProduct.feature || '',
        crank: editProduct.crank || '',
        degree: editProduct.degree || '',
        hinge_type: editProduct.hinge_type || '',
        door_thickness: editProduct.door_thickness || '',
        pullout_type: editProduct.pullout_type || '',
        sliding_type: editProduct.sliding_type || '',
        size_ft: editProduct.size_ft || '',
        door_type: editProduct.door_type || '',
        basket_type: editProduct.basket_type || '',
        material: editProduct.material || '',
        accessory_type: editProduct.accessory_type || '',
        size_inches: editProduct.size_inches || '',
        show_brand_in_pdf: editProduct.show_brand_in_pdf !== false,
      });
      setBrandInput(editProduct.brand_name || '');
      setVariants(editProduct.hardware_system_variants?.length > 0 
        ? editProduct.hardware_system_variants 
        : [{ ...emptyVariant }]
      );
    } else if (open) {
      resetForm();
    }
  }, [editProduct, open]);

  const resetForm = () => {
    setForm({
      brand_name: '', hardware_system_type: 'tandem', series: '', product_name: '',
      load_capacity: '', depth: '', height: '', width: '', color: '', feature: '',
      crank: '', degree: '', hinge_type: '', door_thickness: '',
      pullout_type: '', sliding_type: '', size_ft: '', door_type: '',
      basket_type: '', material: '', accessory_type: '', size_inches: '',
      show_brand_in_pdf: true,
    });
    setBrandInput('');
    setVariants([{ ...emptyVariant }]);
    setRateUnitOverride('');
  };

  const getCurrentType = () => hardwareTypes.find(t => t.id === form.hardware_system_type) || hardwareTypes[0];
  const getCurrentFields = () => {
    const type = getCurrentType();
    return type.fields || DEFAULT_TYPE_FIELDS[form.hardware_system_type] || [];
  };

  const addVariant = () => setVariants([...variants, { ...emptyVariant }]);
  const removeVariant = (index) => variants.length > 1 && setVariants(variants.filter((_, i) => i !== index));
  const updateVariant = (index, field, value) => {
    const updated = [...variants];
    updated[index] = { ...updated[index], [field]: value };
    setVariants(updated);
  };

  const handleBrandSelect = (brand) => {
    setForm(f => ({ ...f, brand_name: brand }));
    setBrandInput(brand);
    setShowBrandDropdown(false);
  };

  const addCustomType = () => {
    if (!customTypeName.trim()) {
      toast.error('Please enter a type name');
      return;
    }
    const newType = {
      id: customTypeName.toLowerCase().replace(/\s+/g, '_'),
      label: customTypeName,
      icon: 'Settings',
      rateUnit: customTypeRateUnit,
      fields: customTypeFields,
      isCustom: true,
    };
    setHardwareTypes([...hardwareTypes, newType]);
    setForm(f => ({ ...f, hardware_system_type: newType.id }));
    setShowCustomTypeDialog(false);
    setCustomTypeName('');
    setCustomTypeFields([]);
    toast.success(`Added "${customTypeName}" type`);
  };

  const toggleCustomField = (fieldId) => {
    setCustomTypeFields(prev => 
      prev.includes(fieldId) ? prev.filter(f => f !== fieldId) : [...prev, fieldId]
    );
  };

  // Fetch existing hardware products for "Copy from Previous"
  const fetchExistingHardwareProducts = async () => {
    setLoadingProducts(true);
    try {
      const response = await api.getProducts({ category: 'Hardware', limit: 100 });
      // Filter only hardware system products (those with hardware_system_type)
      const products = response.data.products || response.data.items || [];
      const hardwareSystemProducts = products.filter(
        p => p.hardware_system_type && p.hardware_system_variants?.length > 0
      );
      setExistingHardwareProducts(hardwareSystemProducts);
    } catch (error) {
      logger.error('Error fetching hardware products:', error);
      toast.error('Could not load existing products');
    } finally {
      setLoadingProducts(false);
    }
  };

  // Open copy dialog and fetch products
  const handleCopyFromPrevious = () => {
    setShowCopyDialog(true);
    setCopySearchTerm('');
    fetchExistingHardwareProducts();
  };

  // Copy specs from selected product (but NOT sizes/rates)
  const copyFromProduct = (product) => {
    setForm(f => ({
      ...f,
      brand_name: product.brand_name || '',
      hardware_system_type: product.hardware_system_type || 'tandem',
      series: product.series || product.subcategory || '',
      // Copy all specifications
      load_capacity: product.load_capacity || '',
      depth: product.depth || '',
      height: product.height || '',
      width: product.width || '',
      color: product.color || '',
      feature: product.feature || '',
      crank: product.crank || '',
      degree: product.degree || '',
      hinge_type: product.hinge_type || '',
      door_thickness: product.door_thickness || '',
      pullout_type: product.pullout_type || '',
      sliding_type: product.sliding_type || '',
      size_ft: product.size_ft || '',
      door_type: product.door_type || '',
      basket_type: product.basket_type || '',
      material: product.material || '',
      accessory_type: product.accessory_type || '',
      size_inches: product.size_inches || '',
      // Keep product_name empty for user to fill
      product_name: '',
    }));
    setBrandInput(product.brand_name || '');
    
    // Copy rate unit if present
    if (product.rate_type) {
      const matchingUnit = RATE_UNIT_OPTIONS.find(u => u === product.rate_type);
      if (matchingUnit) {
        setRateUnitOverride(matchingUnit);
      }
    }
    
    // Reset variants to empty for user to add new sizes
    setVariants([{ ...emptyVariant }]);
    
    setShowCopyDialog(false);
    toast.success(`Copied specs from "${product.brand_name} ${product.series || product.subcategory}". Now add your sizes & rates.`);
  };

  // Filter products by search term
  const filteredCopyProducts = existingHardwareProducts.filter(p => {
    const searchLower = copySearchTerm.toLowerCase();
    return (
      (p.brand_name || '').toLowerCase().includes(searchLower) ||
      (p.series || '').toLowerCase().includes(searchLower) ||
      (p.subcategory || '').toLowerCase().includes(searchLower) ||
      (p.hardware_system_type || '').toLowerCase().includes(searchLower)
    );
  });

  // Add a new custom field
  const addCustomField = () => {
    if (!newCustomFieldName.trim()) return;
    const fieldId = newCustomFieldName.toLowerCase().replace(/\s+/g, '_');
    const newField = { id: fieldId, label: newCustomFieldName.trim(), isCustom: true };
    
    // Check if field already exists
    if (customFieldsList.find(f => f.id === fieldId) || ALL_AVAILABLE_FIELDS.find(f => f.id === fieldId)) {
      toast.error('This field already exists');
      return;
    }
    
    setCustomFieldsList([...customFieldsList, newField]);
    setCustomTypeFields([...customTypeFields, fieldId]);
    setNewCustomFieldName('');
    toast.success(`Added "${newCustomFieldName}" field`);
  };

  // Remove custom field
  const removeCustomField = (fieldId) => {
    setCustomFieldsList(customFieldsList.filter(f => f.id !== fieldId));
    setCustomTypeFields(customTypeFields.filter(f => f !== fieldId));
  };

  // Get effective rate unit (override or type default)
  const getEffectiveRateUnit = () => {
    if (rateUnitOverride) return rateUnitOverride;
    return getCurrentType().rateUnit;
  };

  const handleSave = async () => {
    if (!form.brand_name.trim()) {
      toast.error('Please enter brand name');
      return;
    }

    const validVariants = variants.filter(v => (v.size_text?.trim() || v.width || v.depth || v.height) && v.rate);
    if (validVariants.length === 0) {
      toast.error('Please add at least one size with rate');
      return;
    }

    setSaving(true);
    try {
      const typeConfig = getCurrentType();
      
      let productName = form.product_name || form.series || '';
      if (!productName) {
        const parts = [form.series, form.load_capacity, form.feature].filter(Boolean);
        productName = parts.join(' ') || typeConfig.label;
      }

      // Collect custom field values
      const customFieldValues = {};
      customFieldsList.forEach(cf => {
        if (form[cf.id]) {
          customFieldValues[cf.id] = form[cf.id];
        }
      });

      const effectiveRateUnit = getEffectiveRateUnit();
      
      const productData = {
        brand_name: form.brand_name.trim().toUpperCase(),
        main_category: 'Hardware',
        subcategory: form.series || typeConfig.label,
        product_name: productName,
        hardware_system_type: form.hardware_system_type,
        series: form.series,
        load_capacity: form.load_capacity, depth: form.depth, height: form.height,
        width: form.width, color: form.color, feature: form.feature,
        crank: form.crank, degree: form.degree, hinge_type: form.hinge_type,
        door_thickness: form.door_thickness, pullout_type: form.pullout_type,
        sliding_type: form.sliding_type, size_ft: form.size_ft, door_type: form.door_type,
        basket_type: form.basket_type, material: form.material,
        accessory_type: form.accessory_type, size_inches: form.size_inches,
        custom_fields: customFieldValues, // Store custom field values
        hardware_system_variants: validVariants.map(v => {
          // Build size text based on hardware type
          let sizeText = v.size_text?.trim();
          if (!sizeText) {
            if (form.hardware_system_type === 'tandem') {
              // Tandem: depth x height
              sizeText = [v.depth, v.height].filter(Boolean).join(' x ');
            } else if (form.hardware_system_type === 'telescopic') {
              // Telescopic: size + feature + weight
              const parts = [v.size_text, v.feature === 'soft_close' ? 'Soft Close' : 'Regular'];
              if (v.weight) parts.push(v.weight);
              sizeText = parts.filter(Boolean).join(' ');
            } else if (form.hardware_system_type === 'hinge') {
              // Hinge: crank + type
              const type = v.hinge_mount === 'clip_on' ? 'Clip-on' : 'Slide-on';
              sizeText = [v.size_text, type].filter(Boolean).join(' ');
            } else {
              sizeText = [v.depth, v.height].filter(Boolean).join(' x ');
            }
          }
          return {
            width: v.width, 
            depth: v.depth, 
            height: v.height,
            size_text: sizeText,
            feature: v.feature || '',
            weight: v.weight || '',
            hinge_mount: v.hinge_mount || '',
            rate: parseFloat(v.rate) || 0,
          };
        }),
        rate_type: effectiveRateUnit,
        base_rate: parseFloat(validVariants[0]?.rate) || 0,
        status: 'active',
        gst_included: true,
        show_brand_in_pdf: form.show_brand_in_pdf !== false,
      };

      if (editProduct?.id) {
        await api.updateProduct(editProduct.id, productData);
        toast.success('Hardware product updated!');
      } else {
        await api.createProduct(productData);
        toast.success('Hardware product added!');
      }

      onSuccess?.();
      onOpenChange(false);
    } catch (err) {
      toast.error(err.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const typeConfig = getCurrentType();
  const activeFields = getCurrentFields();

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Package className="w-5 h-5 text-[#556B2F]" />
            {editProduct ? 'Edit' : 'Add'} Hardware Product
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Copy from Previous Button - only show for new products */}
          {!editProduct && (
            <div className="flex justify-end">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={handleCopyFromPrevious}
                className="text-xs border-[#DAA520] text-[#8B6914] hover:bg-[#DAA520]/10"
                data-testid="copy-from-previous-btn"
              >
                <Copy className="w-3 h-3 mr-1" /> Copy from Previous
              </Button>
            </div>
          )}

          {/* Hardware Type Selection */}
          <div className="bg-[#F5F7F2] p-4 rounded-lg">
            <Label className="text-xs font-semibold uppercase tracking-wide text-[#5C6150]">
              Hardware Type
            </Label>
            <div className="grid grid-cols-4 gap-2 mt-2">
              {hardwareTypes.map(type => {
                const IconComponent = ICON_COMPONENTS[type.icon] || Settings;
                return (
                  <button
                    key={type.id}
                    type="button"
                    onClick={() => setForm(f => ({ ...f, hardware_system_type: type.id }))}
                    className={`p-2 rounded-lg border text-center transition-all ${
                      form.hardware_system_type === type.id
                        ? 'border-[#556B2F] bg-[#556B2F] text-white'
                        : 'border-[#E2E6D9] bg-white hover:border-[#556B2F]/50'
                    }`}
                  >
                    <IconComponent className="w-5 h-5 mx-auto" />
                    <p className="text-[10px] mt-1 leading-tight">{type.label}</p>
                  </button>
                );
              })}
              {/* Add Custom Type Button */}
              <button
                type="button"
                onClick={() => setShowCustomTypeDialog(true)}
                className="p-2 rounded-lg border border-dashed border-[#556B2F]/50 text-center hover:bg-[#556B2F]/5 transition-all"
              >
                <Plus className="w-5 h-5 mx-auto text-[#556B2F]" />
                <p className="text-[10px] mt-1 leading-tight text-[#556B2F]">Add Type</p>
              </button>
            </div>
            <p className="text-xs text-[#8F9485] mt-2">Default Rate: {typeConfig.rateUnit}</p>
          </div>

          {/* Rate Unit Override Selector */}
          <div className="bg-[#FEFCE8] p-3 rounded-lg border border-[#EAB308]/30">
            <Label className="text-xs font-semibold uppercase tracking-wide text-[#8B6914]">
              Rate Unit {rateUnitOverride && <span className="text-[#556B2F]">(Overridden)</span>}
            </Label>
            <div className="flex flex-wrap gap-2 mt-2">
              {RATE_UNIT_OPTIONS.map(unit => (
                <button
                  key={unit}
                  type="button"
                  onClick={() => setRateUnitOverride(unit === typeConfig.rateUnit ? '' : unit)}
                  className={`px-3 py-1.5 text-xs rounded-full transition-all ${
                    getEffectiveRateUnit() === unit
                      ? 'bg-[#556B2F] text-white'
                      : 'bg-white border border-[#E2E6D9] hover:border-[#556B2F]/50'
                  }`}
                >
                  {unit}
                </button>
              ))}
            </div>
            {rateUnitOverride && (
              <p className="text-xs text-[#8B6914] mt-2">
                Using "{rateUnitOverride}" instead of default "{typeConfig.rateUnit}"
              </p>
            )}
            {/* Pieces per Box - show when per box is selected */}
            {getEffectiveRateUnit() === 'per box' && (
              <div className="mt-3 pt-3 border-t border-[#EAB308]/30">
                <Label className="text-xs">Pieces per Box</Label>
                <Input
                  type="number"
                  placeholder="e.g., 100"
                  value={form.pieces_per_box || ''}
                  onChange={e => setForm(f => ({ ...f, pieces_per_box: parseInt(e.target.value) || 0 }))}
                  className="mt-1 text-sm w-32"
                  data-testid="hardware-pieces-per-box"
                />
                <p className="text-[10px] text-[#8B6914] mt-1">How many pieces in one box</p>
              </div>
            )}
          </div>

          {/* Brand & Series */}
          <div className="grid grid-cols-2 gap-3">
            <div className="relative">
              <Label className="text-xs">Brand *</Label>
              <Input
                value={brandInput}
                onChange={e => {
                  setBrandInput(e.target.value.toUpperCase());
                  setForm(f => ({ ...f, brand_name: e.target.value.toUpperCase() }));
                  setShowBrandDropdown(true);
                }}
                onFocus={() => setShowBrandDropdown(true)}
                onBlur={() => setTimeout(() => setShowBrandDropdown(false), 200)}
                placeholder="e.g., BLUM, HETTICH"
                className="mt-1"
              />
              {showBrandDropdown && (
                <div className="absolute z-50 w-full mt-1 bg-white border border-[#E2E6D9] rounded-lg shadow-lg max-h-40 overflow-y-auto">
                  {BRANDS.filter(b => b.includes(brandInput.toUpperCase())).map(brand => (
                    <button key={brand} type="button" onClick={() => handleBrandSelect(brand)}
                      className="w-full px-3 py-2 text-left text-sm hover:bg-[#F5F7F2]">{brand}</button>
                  ))}
                </div>
              )}
            </div>
            <div>
              <Label className="text-xs">Series / Model</Label>
              <Input value={form.series} onChange={e => setForm(f => ({ ...f, series: e.target.value.toUpperCase() }))}
                placeholder="e.g., LEGRABOX, AVANTECH" className="mt-1" />
            </div>
          </div>

          {/* Dynamic Specifications */}
          <div className="bg-[#FAFBF8] p-3 rounded-lg border border-[#E2E6D9]">
            <Label className="text-xs font-semibold uppercase tracking-wide text-[#5C6150]">
              Specifications (type or select)
            </Label>
            
            <div className="grid grid-cols-3 gap-3 mt-2">
              {activeFields.includes('load_capacity') && (
                <div>
                  <Label className="text-[10px] text-[#8F9485]">Load Capacity</Label>
                  <SuggestionInput value={form.load_capacity} onChange={v => setForm(f => ({ ...f, load_capacity: v }))}
                    suggestions={FIELD_SUGGESTIONS.load_capacity} placeholder="e.g., 50 KG" className="h-9 mt-1 text-sm" />
                </div>
              )}
              {activeFields.includes('color') && (
                <div>
                  <Label className="text-[10px] text-[#8F9485]">Color/Finish</Label>
                  <SuggestionInput value={form.color} onChange={v => setForm(f => ({ ...f, color: v }))}
                    suggestions={FIELD_SUGGESTIONS.color} placeholder="e.g., Anthracite" className="h-9 mt-1 text-sm" />
                </div>
              )}
              {activeFields.includes('feature') && (
                <div>
                  <Label className="text-[10px] text-[#8F9485]">Feature</Label>
                  <SuggestionInput value={form.feature} onChange={v => setForm(f => ({ ...f, feature: v }))}
                    suggestions={FIELD_SUGGESTIONS.feature} placeholder="e.g., Soft Close" className="h-9 mt-1 text-sm" />
                </div>
              )}
              {activeFields.includes('size_inches') && (
                <div>
                  <Label className="text-[10px] text-[#8F9485]">Size (Inches)</Label>
                  <SuggestionInput value={form.size_inches} onChange={v => setForm(f => ({ ...f, size_inches: v }))}
                    suggestions={FIELD_SUGGESTIONS.size_inches} placeholder='e.g., 18"' className="h-9 mt-1 text-sm" />
                </div>
              )}
              {activeFields.includes('hinge_type') && (
                <div>
                  <Label className="text-[10px] text-[#8F9485]">Hinge Type</Label>
                  <SuggestionInput value={form.hinge_type} onChange={v => setForm(f => ({ ...f, hinge_type: v }))}
                    suggestions={FIELD_SUGGESTIONS.hinge_type} placeholder="e.g., Clip-on" className="h-9 mt-1 text-sm" />
                </div>
              )}
              {activeFields.includes('crank') && (
                <div>
                  <Label className="text-[10px] text-[#8F9485]">Crank</Label>
                  <SuggestionInput value={form.crank} onChange={v => setForm(f => ({ ...f, crank: v }))}
                    suggestions={FIELD_SUGGESTIONS.crank} placeholder="e.g., 9.5 Crank" className="h-9 mt-1 text-sm" />
                </div>
              )}
              {activeFields.includes('degree') && (
                <div>
                  <Label className="text-[10px] text-[#8F9485]">Degree</Label>
                  <SuggestionInput value={form.degree} onChange={v => setForm(f => ({ ...f, degree: v }))}
                    suggestions={FIELD_SUGGESTIONS.degree} placeholder="e.g., 110°" className="h-9 mt-1 text-sm" />
                </div>
              )}
              {activeFields.includes('pullout_type') && (
                <div>
                  <Label className="text-[10px] text-[#8F9485]">Pullout Type</Label>
                  <SuggestionInput value={form.pullout_type} onChange={v => setForm(f => ({ ...f, pullout_type: v }))}
                    suggestions={FIELD_SUGGESTIONS.pullout_type} placeholder="e.g., Bottle" className="h-9 mt-1 text-sm" />
                </div>
              )}
              {activeFields.includes('width') && (
                <div>
                  <Label className="text-[10px] text-[#8F9485]">Width</Label>
                  <SuggestionInput value={form.width} onChange={v => setForm(f => ({ ...f, width: v }))}
                    suggestions={FIELD_SUGGESTIONS.width} placeholder="e.g., 450mm" className="h-9 mt-1 text-sm" />
                </div>
              )}
              {activeFields.includes('sliding_type') && (
                <div>
                  <Label className="text-[10px] text-[#8F9485]">Sliding Type</Label>
                  <SuggestionInput value={form.sliding_type} onChange={v => setForm(f => ({ ...f, sliding_type: v }))}
                    suggestions={FIELD_SUGGESTIONS.sliding_type} placeholder="e.g., Top Sliding" className="h-9 mt-1 text-sm" />
                </div>
              )}
              {activeFields.includes('size_ft') && (
                <div>
                  <Label className="text-[10px] text-[#8F9485]">Size (FT)</Label>
                  <SuggestionInput value={form.size_ft} onChange={v => setForm(f => ({ ...f, size_ft: v }))}
                    suggestions={FIELD_SUGGESTIONS.size_ft} placeholder="e.g., 8 FT" className="h-9 mt-1 text-sm" />
                </div>
              )}
              {activeFields.includes('door_type') && (
                <div>
                  <Label className="text-[10px] text-[#8F9485]">Door Type</Label>
                  <SuggestionInput value={form.door_type} onChange={v => setForm(f => ({ ...f, door_type: v }))}
                    suggestions={FIELD_SUGGESTIONS.door_type} placeholder="e.g., Single" className="h-9 mt-1 text-sm" />
                </div>
              )}
              {activeFields.includes('basket_type') && (
                <div>
                  <Label className="text-[10px] text-[#8F9485]">Basket Type</Label>
                  <SuggestionInput value={form.basket_type} onChange={v => setForm(f => ({ ...f, basket_type: v }))}
                    suggestions={FIELD_SUGGESTIONS.basket_type} placeholder="e.g., Wire Basket" className="h-9 mt-1 text-sm" />
                </div>
              )}
              {activeFields.includes('material') && (
                <div>
                  <Label className="text-[10px] text-[#8F9485]">Material</Label>
                  <SuggestionInput value={form.material} onChange={v => setForm(f => ({ ...f, material: v }))}
                    suggestions={FIELD_SUGGESTIONS.material} placeholder="e.g., Stainless Steel" className="h-9 mt-1 text-sm" />
                </div>
              )}
              {activeFields.includes('accessory_type') && (
                <div className="col-span-2">
                  <Label className="text-[10px] text-[#8F9485]">Accessory Type</Label>
                  <SuggestionInput value={form.accessory_type} onChange={v => setForm(f => ({ ...f, accessory_type: v }))}
                    suggestions={FIELD_SUGGESTIONS.accessory_type} placeholder="e.g., Shoe Rack" className="h-9 mt-1 text-sm" />
                </div>
              )}
              
              {/* Custom fields from custom types */}
              {customFieldsList.filter(cf => activeFields.includes(cf.id)).map(field => (
                <div key={field.id}>
                  <Label className="text-[10px] text-[#DAA520]">{field.label}</Label>
                  <Input 
                    value={form[field.id] || ''} 
                    onChange={e => setForm(f => ({ ...f, [field.id]: e.target.value }))}
                    placeholder={`Enter ${field.label.toLowerCase()}`} 
                    className="h-9 mt-1 text-sm" 
                  />
                </div>
              ))}
            </div>
          </div>

          {/* Sizes & Rates - Different layout based on hardware type */}
          <div>
            <div className="flex items-center justify-between">
              <Label className="text-xs font-semibold uppercase tracking-wide text-[#5C6150]">
                {form.hardware_system_type === 'hinge' ? 'Crank & Type Variants' : 
                 form.hardware_system_type === 'telescopic' ? 'Size & Feature Variants' :
                 form.hardware_system_type === 'tandem' ? 'Depth & Height Variants' :
                 'Sizes & Rates'} ({getEffectiveRateUnit()})
              </Label>
              <Button type="button" variant="outline" size="sm" onClick={addVariant} className="h-7 text-xs">
                <Plus className="w-3 h-3 mr-1" /> Add Variant
              </Button>
            </div>

            <div className="mt-2 space-y-2">
              {/* TANDEM: Depth + Height only */}
              {form.hardware_system_type === 'tandem' && (
                <>
                  <div className="grid grid-cols-12 gap-2 text-[10px] font-semibold text-[#5C6150] uppercase px-1">
                    <div className="col-span-4">Depth (mm)</div>
                    <div className="col-span-4">Height (mm)</div>
                    <div className="col-span-3">Rate (₹)</div>
                    <div className="col-span-1"></div>
                  </div>
                  {variants.map((variant, index) => (
                    <div key={index} className="grid grid-cols-12 gap-2 items-center">
                      <Input value={variant.depth} onChange={e => updateVariant(index, 'depth', e.target.value)}
                        placeholder="e.g., 350" className="col-span-4 h-9 text-sm" />
                      <Input value={variant.height} onChange={e => updateVariant(index, 'height', e.target.value)}
                        placeholder="e.g., 90.5" className="col-span-4 h-9 text-sm" />
                      <Input type="number" value={variant.rate} onChange={e => updateVariant(index, 'rate', e.target.value)}
                        className="col-span-3 h-9 text-sm font-mono" />
                      <div className="col-span-1 flex justify-end">
                        {variants.length > 1 && (
                          <Button type="button" variant="ghost" size="sm" onClick={() => removeVariant(index)}
                            className="h-8 w-8 p-0 text-red-500"><Trash2 className="w-4 h-4" /></Button>
                        )}
                      </div>
                    </div>
                  ))}
                  <p className="text-[10px] text-[#8F9485] mt-2">
                    Enter depth and height in mm. Rate is per set.
                  </p>
                </>
              )}

              {/* TELESCOPIC: Size + Feature (type-able with suggestions) + optional Weight */}
              {form.hardware_system_type === 'telescopic' && (
                <>
                  <div className="grid grid-cols-12 gap-2 text-[10px] font-semibold text-[#5C6150] uppercase px-1">
                    <div className="col-span-2">Size</div>
                    <div className="col-span-3">Feature</div>
                    <div className="col-span-2">Weight</div>
                    <div className="col-span-3">Rate (₹/pair)</div>
                    <div className="col-span-2"></div>
                  </div>
                  {variants.map((variant, index) => (
                    <div key={index} className="grid grid-cols-12 gap-2 items-center">
                      <Input value={variant.size_text} onChange={e => updateVariant(index, 'size_text', e.target.value)}
                        placeholder='e.g., 12"' className="col-span-2 h-9 text-sm" />
                      <div className="col-span-3">
                        <SuggestionInput 
                          value={variant.feature || ''} 
                          onChange={v => updateVariant(index, 'feature', v)}
                          suggestions={FIELD_SUGGESTIONS.telescopic_feature}
                          placeholder="e.g., Regular" 
                          className="h-9 text-sm" 
                        />
                      </div>
                      <Input value={variant.weight} onChange={e => updateVariant(index, 'weight', e.target.value)}
                        placeholder="e.g., 45 KG" className="col-span-2 h-9 text-sm" />
                      <Input type="number" value={variant.rate} onChange={e => updateVariant(index, 'rate', e.target.value)}
                        placeholder="e.g., 450" className="col-span-3 h-9 text-sm font-mono" />
                      <div className="col-span-2 flex justify-end">
                        {variants.length > 1 && (
                          <Button type="button" variant="ghost" size="sm" onClick={() => removeVariant(index)}
                            className="h-8 w-8 p-0 text-red-500"><Trash2 className="w-4 h-4" /></Button>
                        )}
                      </div>
                    </div>
                  ))}
                  <p className="text-[10px] text-[#8F9485] mt-2">
                    Size (inches), Feature type (type or select), optional Weight. Rate is per pair.
                  </p>
                </>
              )}

              {/* HINGE: Crank + Type (type-able with suggestions) */}
              {form.hardware_system_type === 'hinge' && (
                <>
                  <div className="grid grid-cols-12 gap-2 text-[10px] font-semibold text-[#5C6150] uppercase px-1">
                    <div className="col-span-4">Crank</div>
                    <div className="col-span-4">Type</div>
                    <div className="col-span-3">Rate (₹/pc)</div>
                    <div className="col-span-1"></div>
                  </div>
                  {variants.map((variant, index) => (
                    <div key={index} className="grid grid-cols-12 gap-2 items-center">
                      <div className="col-span-4">
                        <SuggestionInput 
                          value={variant.size_text || ''} 
                          onChange={v => updateVariant(index, 'size_text', v)}
                          suggestions={FIELD_SUGGESTIONS.crank}
                          placeholder="e.g., 0 Crank" 
                          className="h-9 text-sm" 
                        />
                      </div>
                      <div className="col-span-4">
                        <SuggestionInput 
                          value={variant.hinge_mount || ''} 
                          onChange={v => updateVariant(index, 'hinge_mount', v)}
                          suggestions={FIELD_SUGGESTIONS.hinge_mount}
                          placeholder="e.g., Clip-on" 
                          className="h-9 text-sm" 
                        />
                      </div>
                      <Input type="number" value={variant.rate} onChange={e => updateVariant(index, 'rate', e.target.value)}
                        placeholder="e.g., 150" className="col-span-3 h-9 text-sm font-mono" />
                      <div className="col-span-1 flex justify-end">
                        {variants.length > 1 && (
                          <Button type="button" variant="ghost" size="sm" onClick={() => removeVariant(index)}
                            className="h-8 w-8 p-0 text-red-500"><Trash2 className="w-4 h-4" /></Button>
                        )}
                      </div>
                    </div>
                  ))}
                  <p className="text-[10px] text-[#8F9485] mt-2">
                    Crank type + mounting type (type or select). Rate is per piece.
                  </p>
                </>
              )}

              {/* OTHER TYPES: Generic size fields */}
              {!['tandem', 'telescopic', 'hinge', 'pullout', 'sliding'].includes(form.hardware_system_type) && (
                <>
                  <div className="grid grid-cols-12 gap-2 text-[10px] font-semibold text-[#5C6150] uppercase px-1">
                    <div className="col-span-3">Depth</div>
                    <div className="col-span-3">Height</div>
                    <div className="col-span-3">Size Text</div>
                    <div className="col-span-2">Rate (₹)</div>
                    <div className="col-span-1"></div>
                  </div>
                  {variants.map((variant, index) => (
                    <div key={index} className="grid grid-cols-12 gap-2 items-center">
                      <Input value={variant.depth} onChange={e => updateVariant(index, 'depth', e.target.value)}
                        className="col-span-3 h-9 text-sm" />
                      <Input value={variant.height} onChange={e => updateVariant(index, 'height', e.target.value)}
                        className="col-span-3 h-9 text-sm" />
                      <Input value={variant.size_text} onChange={e => updateVariant(index, 'size_text', e.target.value)}
                        className="col-span-3 h-9 text-sm" />
                      <Input type="number" value={variant.rate} onChange={e => updateVariant(index, 'rate', e.target.value)}
                        className="col-span-2 h-9 text-sm font-mono" />
                      <div className="col-span-1 flex justify-end">
                        {variants.length > 1 && (
                          <Button type="button" variant="ghost" size="sm" onClick={() => removeVariant(index)}
                            className="h-8 w-8 p-0 text-red-500"><Trash2 className="w-4 h-4" /></Button>
                        )}
                      </div>
                    </div>
                  ))}
                  <p className="text-[10px] text-[#8F9485] mt-2">
                    Enter dimensions or use Size Text for custom formats.
                  </p>
                </>
              )}

              {/* PULLOUT: Width, Depth, Height, Rate - Width is main differentiator */}
              {form.hardware_system_type === 'pullout' && (
                <>
                  <div className="grid grid-cols-12 gap-2 text-[10px] font-semibold text-[#5C6150] uppercase px-1">
                    <div className="col-span-3">Width (mm)</div>
                    <div className="col-span-2">Depth (mm)</div>
                    <div className="col-span-2">Height (mm)</div>
                    <div className="col-span-3">Rate (₹)</div>
                    <div className="col-span-2"></div>
                  </div>
                  {variants.map((variant, index) => (
                    <div key={index} className="grid grid-cols-12 gap-2 items-center">
                      <Input value={variant.width || ''} onChange={e => updateVariant(index, 'width', e.target.value)}
                        placeholder="e.g., 150" className="col-span-3 h-9 text-sm" />
                      <Input value={variant.depth || ''} onChange={e => updateVariant(index, 'depth', e.target.value)}
                        placeholder="e.g., 500" className="col-span-2 h-9 text-sm" />
                      <Input value={variant.height || ''} onChange={e => updateVariant(index, 'height', e.target.value)}
                        placeholder="e.g., 600" className="col-span-2 h-9 text-sm" />
                      <Input type="number" value={variant.rate} onChange={e => updateVariant(index, 'rate', e.target.value)}
                        className="col-span-3 h-9 text-sm font-mono" />
                      <div className="col-span-2 flex justify-end">
                        {variants.length > 1 && (
                          <Button type="button" variant="ghost" size="sm" onClick={() => removeVariant(index)}
                            className="h-8 w-8 p-0 text-red-500"><Trash2 className="w-4 h-4" /></Button>
                        )}
                      </div>
                    </div>
                  ))}
                  <p className="text-[10px] text-[#8F9485] mt-2">
                    Width is the main differentiator. All dimensions optional, enter what's applicable.
                  </p>
                </>
              )}

              {/* SLIDING: Size (feet) and Rate only */}
              {form.hardware_system_type === 'sliding' && (
                <>
                  <div className="grid grid-cols-12 gap-2 text-[10px] font-semibold text-[#5C6150] uppercase px-1">
                    <div className="col-span-6">Size (feet)</div>
                    <div className="col-span-4">Rate (₹)</div>
                    <div className="col-span-2"></div>
                  </div>
                  {variants.map((variant, index) => (
                    <div key={index} className="grid grid-cols-12 gap-2 items-center">
                      <Input value={variant.size_text || ''} onChange={e => updateVariant(index, 'size_text', e.target.value)}
                        placeholder="e.g., 8, 10, 12" className="col-span-6 h-9 text-sm" />
                      <Input type="number" value={variant.rate} onChange={e => updateVariant(index, 'rate', e.target.value)}
                        className="col-span-4 h-9 text-sm font-mono" />
                      <div className="col-span-2 flex justify-end">
                        {variants.length > 1 && (
                          <Button type="button" variant="ghost" size="sm" onClick={() => removeVariant(index)}
                            className="h-8 w-8 p-0 text-red-500"><Trash2 className="w-4 h-4" /></Button>
                        )}
                      </div>
                    </div>
                  ))}
                  <p className="text-[10px] text-[#8F9485] mt-2">
                    Enter size in feet. Use Sliding Type & Door Type in Specifications above.
                  </p>
                </>
              )}
            </div>
          </div>

          {/* Per-product brand visibility in customer PDF */}
          <div className="flex items-start gap-2 p-3 rounded-md border border-[#E2E6D9] bg-[#F9FAF7]">
            <input
              id="hwsys-show-brand-in-pdf"
              type="checkbox"
              checked={form.show_brand_in_pdf !== false}
              onChange={(e) => setForm(f => ({ ...f, show_brand_in_pdf: e.target.checked }))}
              className="mt-0.5 w-4 h-4 accent-[#556B2F]"
              data-testid="form-show-brand-in-pdf"
            />
            <label htmlFor="hwsys-show-brand-in-pdf" className="text-xs leading-tight cursor-pointer select-none">
              <span className="font-semibold text-[#1A1C16]">Show brand name in customer PDF</span>
              <p className="text-[#5C6150] text-[10px] mt-0.5">
                Turn off for generic items where brand doesn&apos;t matter to the customer. Keep on for branded items like Tandem, Hettich, Hafele.
              </p>
            </label>
          </div>

          {/* Save Button */}
          <Button onClick={handleSave} disabled={saving} className="w-full bg-[#556B2F] hover:bg-[#465926] text-white">
            <Save className="w-4 h-4 mr-2" />
            {saving ? 'Saving...' : editProduct ? 'Update Product' : 'Add Product'}
          </Button>
        </div>

        {/* Custom Type Dialog */}
        {showCustomTypeDialog && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[100]">
            <div className="bg-white rounded-lg p-6 w-[500px] max-h-[80vh] overflow-y-auto">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold">Add Custom Hardware Type</h3>
                <Button variant="ghost" size="sm" onClick={() => setShowCustomTypeDialog(false)}>
                  <X className="w-4 h-4" />
                </Button>
              </div>
              
              <div className="space-y-4">
                <div>
                  <Label className="text-xs">Type Name *</Label>
                  <Input value={customTypeName} onChange={e => setCustomTypeName(e.target.value)}
                    placeholder="e.g., Wardrobe Fittings" className="mt-1" />
                </div>
                
                <div>
                  <Label className="text-xs">Rate Unit</Label>
                  <div className="flex gap-2 mt-1">
                    {['per piece', 'per set', 'per pair', 'per meter'].map(unit => (
                      <button key={unit} type="button" onClick={() => setCustomTypeRateUnit(unit)}
                        className={`px-3 py-1.5 text-xs rounded border ${
                          customTypeRateUnit === unit ? 'bg-[#556B2F] text-white border-[#556B2F]' : 'border-[#E2E6D9]'
                        }`}>{unit}</button>
                    ))}
                  </div>
                </div>

                <div>
                  <Label className="text-xs">Select Fields to Include</Label>
                  <div className="mt-2 grid grid-cols-2 gap-2 max-h-40 overflow-y-auto">
                    {ALL_AVAILABLE_FIELDS.map(field => (
                      <label key={field.id} className="flex items-center gap-2 p-2 rounded border border-[#E2E6D9] cursor-pointer hover:bg-[#F5F7F2]">
                        <Checkbox checked={customTypeFields.includes(field.id)}
                          onCheckedChange={() => toggleCustomField(field.id)} />
                        <span className="text-xs">{field.label}</span>
                      </label>
                    ))}
                    {/* Custom fields added by user */}
                    {customFieldsList.map(field => (
                      <div key={field.id} className="flex items-center gap-2 p-2 rounded border border-[#DAA520] bg-[#DAA520]/10">
                        <Checkbox checked={customTypeFields.includes(field.id)}
                          onCheckedChange={() => toggleCustomField(field.id)} />
                        <span className="text-xs flex-1">{field.label}</span>
                        <button type="button" onClick={() => removeCustomField(field.id)}
                          className="text-red-500 hover:text-red-700">
                          <X className="w-3 h-3" />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Add Custom Field */}
                <div className="border-t border-[#E2E6D9] pt-4">
                  <Label className="text-xs font-semibold text-[#DAA520]">Add Custom Field</Label>
                  <div className="flex gap-2 mt-2">
                    <Input 
                      value={newCustomFieldName}
                      onChange={e => setNewCustomFieldName(e.target.value)}
                      placeholder="e.g., Model Number, Weight"
                      className="flex-1 text-sm"
                      onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), addCustomField())}
                    />
                    <Button type="button" variant="outline" onClick={addCustomField}
                      className="border-[#DAA520] text-[#8B6914] hover:bg-[#DAA520]/10">
                      <Plus className="w-4 h-4" />
                    </Button>
                  </div>
                  <p className="text-[10px] text-[#8F9485] mt-1">
                    Create your own fields for this hardware type
                  </p>
                </div>

                <Button onClick={addCustomType} className="w-full bg-[#556B2F] hover:bg-[#465926] text-white">
                  <Plus className="w-4 h-4 mr-2" /> Add Type
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Copy from Previous Dialog */}
        {showCopyDialog && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[100]">
            <div className="bg-white rounded-lg p-6 w-[550px] max-h-[80vh] overflow-hidden flex flex-col">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-[#1A1C16]">Copy from Previous Product</h3>
                <Button variant="ghost" size="sm" onClick={() => setShowCopyDialog(false)}>
                  <X className="w-4 h-4" />
                </Button>
              </div>
              
              <p className="text-sm text-[#5C6150] mb-4">
                Select a product to copy its specs (brand, type, series, specifications). You'll add new sizes & rates after.
              </p>
              
              {/* Search */}
              <div className="mb-4">
                <Input 
                  value={copySearchTerm}
                  onChange={e => setCopySearchTerm(e.target.value)}
                  placeholder="Search by brand, series, or type..."
                  className="w-full"
                  data-testid="copy-search-input"
                />
              </div>
              
              {/* Product List */}
              <div className="flex-1 overflow-y-auto border rounded-lg">
                {loadingProducts ? (
                  <div className="p-8 text-center text-[#8F9485]">Loading products...</div>
                ) : filteredCopyProducts.length === 0 ? (
                  <div className="p-8 text-center text-[#8F9485]">
                    {existingHardwareProducts.length === 0 
                      ? 'No hardware system products found. Add your first product manually.'
                      : 'No products match your search.'}
                  </div>
                ) : (
                  <div className="divide-y divide-[#E2E6D9]">
                    {filteredCopyProducts.map((product) => (
                      <button
                        key={product._id}
                        type="button"
                        onClick={() => copyFromProduct(product)}
                        className="w-full p-3 text-left hover:bg-[#F5F7F2] transition-colors"
                        data-testid={`copy-product-${product._id}`}
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <p className="font-semibold text-[#1A1C16]">{product.brand_name}</p>
                            <p className="text-sm text-[#5C6150]">{product.series || product.subcategory}</p>
                            <div className="flex flex-wrap gap-1 mt-1">
                              <span className="text-xs px-2 py-0.5 bg-[#556B2F]/10 text-[#556B2F] rounded capitalize">
                                {product.hardware_system_type?.replace(/_/g, ' ')}
                              </span>
                              {product.load_capacity && (
                                <span className="text-xs px-2 py-0.5 bg-gray-100 rounded">{product.load_capacity}</span>
                              )}
                              {product.feature && (
                                <span className="text-xs px-2 py-0.5 bg-gray-100 rounded">{product.feature}</span>
                              )}
                              {product.color && (
                                <span className="text-xs px-2 py-0.5 bg-gray-100 rounded">{product.color}</span>
                              )}
                            </div>
                          </div>
                          <div className="text-xs text-[#8F9485] text-right">
                            <p>{product.hardware_system_variants?.length || 0} sizes</p>
                            <p>₹{product.base_rate?.toLocaleString('en-IN')}</p>
                          </div>
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
              
              <p className="text-xs text-[#8F9485] mt-3 text-center">
                Click a product to copy its specs. Sizes & rates will need to be added separately.
              </p>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
